import React from 'react';
import type { CashFlowStatement } from './CashFlowProformaTable';

export type MultifamilyCashFlowScenarios = {
  duplex?: CashFlowStatement;
  triplex?: CashFlowStatement;
  quadplex?: CashFlowStatement;
};

const money = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString()}` : 'N/A';

const moneyPerMonthYear = (monthly: number | null | undefined, annual: number | null | undefined) => {
  if (monthly == null && annual == null) return 'N/A';
  const m = monthly != null ? money(monthly) : money((annual || 0) / 12);
  const y = annual != null ? money(annual) : money((monthly || 0) * 12);
  return `${m}/mo · ${y}/yr`;
};

const pctOfRent = (amount: number | null | undefined, monthlyRent: number | null | undefined) => {
  const rent = Number(monthlyRent);
  const expense = Number(amount);
  if (!Number.isFinite(rent) || rent <= 0 || !Number.isFinite(expense)) return '—';
  return `${(Math.round((expense / rent) * 1000) / 10).toFixed(1)}%`;
};

const formatRate = (rate: number | null | undefined) => {
  if (rate == null || !Number.isFinite(Number(rate))) return '—';
  const pct = Math.round(Number(rate) * 1000) / 1000;
  return `${pct}%`;
};

const SCENARIO_ORDER: Array<{ key: keyof MultifamilyCashFlowScenarios; label: string }> = [
  { key: 'duplex', label: 'Duplex (2 units)' },
  { key: 'triplex', label: 'Triplex (3 units)' },
  { key: 'quadplex', label: 'Quadplex (4 units)' },
];

const cellStyle: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'top', fontSize: 12 };
const headerStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontWeight: 600,
  background: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  textAlign: 'left',
  fontSize: 12,
};

const MultifamilyCashFlowTable: React.FC<{
  scenarios: MultifamilyCashFlowScenarios;
  perUnitRent?: number | null;
}> = ({ scenarios, perUnitRent }) => {
  const cols = SCENARIO_ORDER.map(({ key, label }) => ({
    key,
    label,
    cf: scenarios[key],
  })).filter((col) => col.cf);

  if (!cols.length) return null;

  const purchasePrice = cols[0].cf?.purchasePrice;
  const mort = cols[0].cf?.mortgage;

  return (
    <>
      <p style={{ fontSize: 13, color: '#4b5563', marginBottom: 8 }}>
        Purchase price {money(purchasePrice)} · Multifamily
      </p>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
        Unit count is often unavailable from county records. Compare duplex (2), triplex (3), and quadplex (4)
        scenarios below. Gross rent sums per-unit estimates. When unit mix is unknown, each scenario
        uses the same reference unit size (from property beds/baths/sqft) × door count — so more units
        means more total rent. Lot size is property-level and not multiplied.
        {perUnitRent != null ? (
          <>
            {' '}
            Average per-unit rent ≈ {money(perUnitRent)}/mo.
          </>
        ) : null}
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            border: '1px solid #e5e7eb',
            minWidth: 640,
          }}
        >
          <thead>
            <tr>
              <th style={{ ...headerStyle, width: 120 }}>Line item</th>
              {cols.map((col) => (
                <th key={col.key} style={headerStyle}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ ...cellStyle, fontWeight: 600 }}>Gross rent</td>
              {cols.map((col) => (
                <td key={col.key} style={cellStyle}>
                  {moneyPerMonthYear(col.cf!.monthlyRent, col.cf!.annualRent)}
                </td>
              ))}
            </tr>
            {(cols[0].cf?.expenseLines || []).map((line, idx) => (
              <tr key={line.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={cellStyle}>{line.label}</td>
                {cols.map((col) => {
                  const expenseLine = col.cf?.expenseLines?.[idx];
                  if (!expenseLine) return <td key={col.key} style={cellStyle}>—</td>;
                  return (
                    <td key={col.key} style={cellStyle}>
                      <div style={{ color: '#6b7280', marginBottom: 2 }}>
                        {pctOfRent(expenseLine.monthly, col.cf?.monthlyRent)} of rent
                      </div>
                      {moneyPerMonthYear(expenseLine.monthly, expenseLine.annual)}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
              <td style={{ ...cellStyle, fontWeight: 600 }}>Net operating income</td>
              {cols.map((col) => (
                <td key={col.key} style={{ ...cellStyle, fontWeight: 600 }}>
                  {moneyPerMonthYear(col.cf!.noiMonthly, col.cf!.noiAnnual)}
                </td>
              ))}
            </tr>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={cellStyle}>
                Mortgage ({mort?.downPaymentPct}% down @ {formatRate(mort?.interestRate)} / {mort?.termYears} yrs)
              </td>
              {cols.map((col) => (
                <td key={col.key} style={cellStyle}>
                  {money(col.cf?.mortgage?.monthlyPayment)}/mo
                </td>
              ))}
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ ...cellStyle, fontWeight: 700 }}>Cash flow</td>
              {cols.map((col) => {
                const negative = (col.cf?.cashFlowAnnual ?? 0) < 0;
                return (
                  <td
                    key={col.key}
                    style={{
                      ...cellStyle,
                      fontWeight: 700,
                      color: negative ? '#b91c1c' : '#15803d',
                    }}
                  >
                    {moneyPerMonthYear(col.cf!.cashFlowMonthly, col.cf!.cashFlowAnnual)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td style={cellStyle}>Cash-on-cash return</td>
              {cols.map((col) => (
                <td key={col.key} style={cellStyle}>
                  {col.cf?.cashOnCashReturn != null ? `${col.cf.cashOnCashReturn}%` : 'N/A'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default MultifamilyCashFlowTable;
